'use client';

import React from 'react';
import { cn } from '@/lib/utils';
import { GOAL_LIST } from '@/lib/aviado/goals';
import { useBrowseFilters, type TimeBucket } from '@/lib/hooks/useBrowseFilters';
import type { EvidenceTier, ArticleKind } from '@/lib/types';

/**
 * FilterBar — top-rail dropdown row for Browse.
 *
 * Spec: browse.html `.toprail` + `.toprail__row` (the shipping variant).
 *   - 4 selects: Tier / Goal / Kind / Reading time
 *   - Right: Clear button (only shows when any filter is active)
 *
 * Native `<select>` elements with a custom SVG chevron (styled via globals.css
 * would be cleaner, but inline here keeps the one-file story tight).
 */

const TIER_OPTS: { value: EvidenceTier; label: string }[] = [
  { value: 'strong', label: 'Strong' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'preliminary', label: 'Preliminary' },
  { value: 'insufficient', label: 'Insufficient' },
];

const KIND_OPTS: { value: ArticleKind; label: string }[] = [
  { value: 'knowledge-base', label: 'Knowledge Base' },
  { value: 'evidence-dossier', label: 'Evidence Dossier' },
  { value: 'protocol-brief', label: 'Protocol Brief' },
  { value: 'mechanism', label: 'Mechanism of Action' },
  { value: 'dose', label: 'Dose & Form' },
  { value: 'safety', label: 'Safety & Interactions' },
  { value: 'evidence-summary', label: 'Evidence Summary' },
  { value: 'biomarker-impact', label: 'Biomarker Impact' },
  { value: 'comparison', label: 'Comparison' },
  { value: 'responder', label: 'Responder Variability' },
  { value: 'explainer', label: 'Explainer' },
  { value: 'qa', label: 'Q&A' },
];

const TIME_OPTS: { value: TimeBucket; label: string }[] = [
  { value: '<5', label: '< 5 min' },
  { value: '5-15', label: '5–15 min' },
  { value: '15+', label: '15+ min' },
];

export function FilterBar() {
  const { filters, set, clear } = useBrowseFilters();

  const anyActive = Boolean(
    filters.goal || filters.tier || filters.kind || filters.time || filters.q || filters.supplement || filters.topic
  );

  return (
    <section
      className={cn(
        'sticky top-[72px] z-30',
        'bg-[rgba(247,244,240,0.9)] backdrop-blur-md',
        'pb-4 pt-4 -mt-4'
      )}
    >
      <div className="max-w-container mx-auto px-10">
        <div className="p-5 rounded-xl bg-white border border-border">
          <div className="grid grid-cols-2 lg:grid-cols-[repeat(4,1fr)_auto] gap-4 items-end">
            <SelectCol
              label="Evidence tier"
              value={filters.tier ?? ''}
              placeholder="All tiers"
              options={TIER_OPTS}
              onChange={(v) => set({ tier: (v as EvidenceTier) || null })}
            />
            <SelectCol
              label="Health goal"
              value={filters.goal ?? ''}
              placeholder="All goals"
              options={GOAL_LIST.map((g) => ({ value: g.slug, label: g.label }))}
              onChange={(v) => set({ goal: (v as typeof filters.goal) || null })}
            />
            <SelectCol
              label="Kind"
              value={filters.kind ?? ''}
              placeholder="All kinds"
              options={KIND_OPTS}
              onChange={(v) => set({ kind: (v as ArticleKind) || null })}
            />
            <SelectCol
              label="Reading time"
              value={filters.time ?? ''}
              placeholder="Any length"
              options={TIME_OPTS}
              onChange={(v) => set({ time: (v as TimeBucket) || null })}
            />
            <button
              type="button"
              onClick={clear}
              disabled={!anyActive}
              className={cn(
                'font-ui text-[13px] font-medium',
                'bg-transparent border border-border-strong rounded-md px-4 py-[10px]',
                'text-ink cursor-pointer whitespace-nowrap',
                'hover:bg-neutral-25 transition-colors duration-fast',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

interface SelectColProps {
  label: string;
  value: string;
  placeholder: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}

function SelectCol({ label, value, placeholder, options, onChange }: SelectColProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="font-mono text-[10.5px] font-medium tracking-[0.14em] uppercase text-slate">
        {label}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cn(
            'w-full appearance-none cursor-pointer',
            'font-ui text-[13px] text-ink',
            'px-3 py-[10px] pr-8 rounded-md',
            'bg-white border border-border-strong',
            'focus:outline-none focus:border-aviado-blue focus:shadow-focus',
            'transition-all duration-fast'
          )}
        >
          <option value="">{placeholder}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <svg
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-ink"
          width="10"
          height="6"
          viewBox="0 0 10 6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <path d="M1 1l4 4 4-4" />
        </svg>
      </div>
    </div>
  );
}

export default FilterBar;
